import { useCallback, useRef, useState } from 'react';
import { Button, Flex, Text, TextInput } from '@mantine/core';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { StepBody } from './StepBody';
import { StepFooter } from './StepFooter';
import { StepHeader } from './StepHeader';
import { StepHeading, StepSubHeading } from './typography';
import { SignerContactType } from '@dealstack/shared/lib/generated';
import type { OtpResendOutcome } from '../EidEasySigningModal.types';
import { useResendCooldown } from './useResendCooldown';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';

interface Props {
	channel: SignerContactType;
	maskedContact: string;
	onSubmit: (code: string) => void;
	onResend: () => Promise<OtpResendOutcome>;
	verifying: boolean;
	codeError: string | null;
	onGoBack: () => void;
	hideStepHeader?: boolean;
}

const SMS_CODE_REGEX = /^[0-9]{4,8}$/;
const SMS_CODE_MAX_LENGTH = 8;
const EMAIL_CODE_MAX_LENGTH = 24;
const OTP_RESEND_COOLDOWN_SECONDS = 60;

export const OtpCodeEntryStep = ({
	channel,
	maskedContact,
	onSubmit,
	onResend,
	verifying,
	codeError,
	onGoBack,
	hideStepHeader,
}: Props) => {
	const tCommonUi = useTranslationCommonUi();
	const [code, setCode] = useState('');
	const [resending, setResending] = useState(false);
	// Set when eID Easy rejects a too-soon resend ("wait N seconds"); shown as a
	// notice next to the resend control and cleared once a resend succeeds or the
	// user types again.
	const [resendNotice, setResendNotice] = useState<string | null>(null);
	// Suppress the inline error once the user starts typing, so they don't see
	// a stale "wrong code" message while composing their next attempt.
	// suppressedErrorRef holds the error value the user acknowledged by typing.
	// prevCodeErrorRef detects when codeError changes so we can reset suppression
	// whenever the parent clears the error (null) — ensuring the next error
	// the parent sets is always displayed fresh.
	const suppressedErrorRef = useRef<string | null>(null);
	const prevCodeErrorRef = useRef<string | null | undefined>(undefined);
	if (prevCodeErrorRef.current !== codeError) {
		if (codeError === null) {
			suppressedErrorRef.current = null;
		}
		prevCodeErrorRef.current = codeError;
	}
	const { secondsRemaining, canResend, startCooldown } = useResendCooldown(
		OTP_RESEND_COOLDOWN_SECONDS
	);

	const isEmail = channel === SignerContactType.EMAIL;
	const maxLength = isEmail ? EMAIL_CODE_MAX_LENGTH : SMS_CODE_MAX_LENGTH;
	const sanitise = (value: string) =>
		isEmail ? value.replace(/\s/g, '') : value.replace(/\D/g, '');

	const canSubmit = (isEmail ? code.length > 0 : SMS_CODE_REGEX.test(code)) && !verifying;

	const handleSubmit = useCallback(() => {
		if (canSubmit) {
			onSubmit(code);
		}
	}, [code, canSubmit, onSubmit]);

	const handleResend = useCallback(async () => {
		if (!canResend || resending) {
			return;
		}
		setResending(true);
		setResendNotice(null);
		const outcome = await onResend();
		setResending(false);
		if (outcome.status === 'sent') {
			setCode('');
			startCooldown();
		} else if (outcome.status === 'rate_limited') {
			// Re-arm the countdown to eID Easy's actual remaining window and explain
			// why a new code can't be sent yet.
			startCooldown(outcome.waitSeconds);
			setResendNotice(tCommonUi('eid_easy_signing.otp_resend_wait'));
		}
		// 'failed' → the hook surfaced a fatal error and switched to the error step;
		// nothing to do here.
	}, [canResend, resending, startCooldown, onResend, tCommonUi]);

	const channelLabel =
		channel === SignerContactType.SMS
			? tCommonUi('eid_easy_signing.otp_code_sent_sms')
			: tCommonUi('eid_easy_signing.otp_code_sent_email');

	return (
		<Flex direction="column" w="100%" h="100%">
			{!hideStepHeader && <StepHeader onGoBack={onGoBack} />}
			<StepBody>
				<Flex direction="column" gap={4}>
					<StepHeading>
						{tCommonUi('eid_easy_signing.otp_code_entry_heading')}
					</StepHeading>
					<StepSubHeading>
						{channelLabel} <strong>{maskedContact}</strong>
					</StepSubHeading>
				</Flex>
				<TextInput
					data-testid={TEST_ELEMENT_IDS.EID_EASY_OTP_CODE_INPUT}
					label={tCommonUi('eid_easy_signing.otp_code_label')}
					placeholder={isEmail ? 'r1ggjxhoocxhq3iy' : '123456'}
					value={code}
					inputMode={isEmail ? 'text' : 'numeric'}
					autoComplete="one-time-code"
					autoFocus
					maxLength={maxLength}
					error={
						codeError && suppressedErrorRef.current !== codeError
							? tCommonUi(codeError)
							: undefined
					}
					onChange={(event) => {
						suppressedErrorRef.current = codeError;
						setResendNotice(null);
						setCode(sanitise(event.currentTarget.value));
					}}
					onKeyDown={(event) => {
						if (event.key === 'Enter') {
							handleSubmit();
						}
					}}
				/>
				<Flex direction="column" gap={4}>
					<Button
						variant="subtle"
						size="compact-sm"
						disabled={!canResend || resending || verifying}
						onClick={() => void handleResend()}
						style={{ alignSelf: 'flex-start' }}
					>
						{canResend
							? tCommonUi('eid_easy_signing.otp_resend')
							: tCommonUi('eid_easy_signing.otp_resend_in', {
									seconds: secondsRemaining,
								})}
					</Button>
					{resendNotice && !canResend && (
						<Text size="xs" c="dimmed">
							{resendNotice}
						</Text>
					)}
				</Flex>
			</StepBody>
			<StepFooter>
				<Button
					variant="filled"
					fullWidth
					loading={verifying}
					disabled={!canSubmit}
					onClick={handleSubmit}
					data-testid={TEST_ELEMENT_IDS.EID_EASY_OTP_VERIFY_BUTTON}
				>
					{tCommonUi('eid_easy_signing.otp_verify_code')}
				</Button>
			</StepFooter>
		</Flex>
	);
};
